import { AI_COACH_INTRO_PART, AI_COACH_PARTS } from '../types/AICoach'
import type { AICoachPart, AICoachSession, AICoachSessionData } from '../types/AICoach'
import { CoachConversationFlow, GoalScope, GoalStatus, UserAiMemoryKind } from '../types/CoachTypes'
import type { CoachConversation, UserGoal } from '../types/CoachTypes'
import { collapseWhitespace } from './Common'

type AICoachProgressState = Pick<
  AICoachSession | AICoachSessionData,
  'completedParts' | 'currentPart' | 'status' | 'assistantTask'
>

export const normalizeAICoachCompletedParts = (session: AICoachProgressState): AICoachPart[] => {
  const completed = new Set(session.completedParts || [])

  if (typeof session.currentPart === 'number' && session.currentPart > AI_COACH_INTRO_PART) {
    const assistantCompletedCurrentPart =
      session.assistantTask?.status === 'completed' && session.assistantTask.part === session.currentPart
    const lastCompletedPart =
      session.status === 'ai_speaking' && !assistantCompletedCurrentPart
        ? ((session.currentPart - 1) as AICoachPart)
        : session.currentPart

    AI_COACH_PARTS.filter((part) => part <= lastCompletedPart).forEach((part) => completed.add(part))
  }

  const sessionStartedBeforeIntro =
    !completed.has(AI_COACH_INTRO_PART) &&
    (Array.from(completed).some((part) => part > AI_COACH_INTRO_PART) ||
      (typeof session.currentPart === 'number' && session.currentPart > AI_COACH_INTRO_PART))

  if (sessionStartedBeforeIntro) {
    completed.add(AI_COACH_INTRO_PART)
  }

  return AI_COACH_PARTS.filter((part) => completed.has(part))
}

// Single source of truth for the AI-coach session id — keyed per (sprint, activity, team) so each team
// has its own debrief session. Shared by the debrief session service and the completion bridge so they can't drift.
export const buildAICoachSessionId = (sprintId: string, activityId: string, teamId: string): string =>
  `${sprintId}_${activityId}_${teamId}`

// Total attempts per AI-coach pregeneration task (initial run + retries). Shared by the pregeneration
// executor, the stale-session reaper and the backoffice tasks monitor so they can't drift.
export const AI_COACH_PREGEN_MAX_ATTEMPTS = 3

// How long an unfinished coach conversation stays resumable. Shared, because the two halves disagreeing
// is worse than either value: the page auto-started a spoken session "to resume" a thread the server
// then refused to resume, so it opened a new one — a conversation and a greeting per visit.
export const COACH_RESUME_GRACE_MINUTES = 30 // half an hour

// The flows whose job is to settle the user's development goals — ask `isGoalSettingFlow`, never
// `flow === ONBOARDING`, or the second one escapes every downstream guard.
export const GOAL_SETTING_COACH_FLOWS: CoachConversationFlow[] = [
  CoachConversationFlow.ONBOARDING,
  CoachConversationFlow.ONBOARDING_AFTER_GAME
]

export const isGoalSettingFlow = (flow?: CoachConversationFlow): boolean =>
  !!flow && GOAL_SETTING_COACH_FLOWS.includes(flow)

// Every kind of talk the coach page holds; the floating assistant's own talks are started by the assistant.
export const COACH_PAGE_FLOWS: CoachConversationFlow[] = Object.values(CoachConversationFlow).filter(
  (flow) => flow !== CoachConversationFlow.ASSISTANT
)

// Shared so the edit box and the callable that refuses the write agree on the limit.
export const MAX_GOAL_LENGTH_CHARS = 300 // ~2 sentences

// ARCHIVED is left out: it hides a goal from every read, so it is a delete without the confirmation.
export const USER_SETTABLE_GOAL_STATUSES: GoalStatus[] = [GoalStatus.ACTIVE, GoalStatus.ACHIEVED]

// Three active goals at a time per goal scope; more than that stops being a focus.
export const MAX_USER_GOALS = 3

// A goal stored before scopes existed is an individual goal.
export const resolveGoalScope = (goal: Pick<UserGoal, 'scope'>): GoalScope => goal.scope ?? GoalScope.INDIVIDUAL

/** Holding goals of the list a talk works on is what finishes goal-setting, whichever way the goals arrived. */
export const holdsGoalsOfScope = (goals: Pick<UserGoal, 'scope'>[], scope: GoalScope): boolean =>
  goals.some((goal) => resolveGoalScope(goal) === scope)

const getGoalScopeForFlow = (flow: CoachConversationFlow): GoalScope =>
  flow === CoachConversationFlow.LEADERSHIP ? GoalScope.LEADERSHIP : GoalScope.INDIVIDUAL

/** The goal list a talk works on: the scope it was created with, or the one its flow implies. */
export const resolveConversationGoalScope = (conversation: Pick<CoachConversation, 'flow' | 'goalScope'>): GoalScope =>
  conversation.goalScope ?? getGoalScopeForFlow(conversation.flow)

/** A leadership talk: a leadership conversation, or goal-setting done as a leader. */
export const isLeadershipConversation = (conversation?: Pick<CoachConversation, 'flow' | 'goalScope'>): boolean =>
  !!conversation && resolveConversationGoalScope(conversation) === GoalScope.LEADERSHIP

/** A leadership talk and a personal one never continue each other, whichever was the more recent. */
export const isSameTalkKind = (conversation: Pick<CoachConversation, 'flow' | 'goalScope'>, wantsLeadership: boolean) =>
  isLeadershipConversation(conversation) === wantsLeadership

/** A talk held in the floating assistant rather than on the coach page. */
export const isAssistantConversation = (conversation?: Pick<CoachConversation, 'flow'>): boolean =>
  conversation?.flow === CoachConversationFlow.ASSISTANT

const USER_AI_MEMORY_KINDS: string[] = Object.values(UserAiMemoryKind)

export const isUserAiMemoryKind = (value: unknown): value is UserAiMemoryKind =>
  typeof value === 'string' && USER_AI_MEMORY_KINDS.includes(value)

// What makes two goals the same goal to a human: case, spacing and trailing punctuation ignored.
// Shared, because Aria names a goal by its wording and the client has to find the row she means.
export const goalMatchKey = (goal: string): string =>
  collapseWhitespace(goal)
    .toLowerCase()
    .replace(/[.!?,;:]+$/u, '')
    .trim()

// Aria's durable memories are free text she writes about the person. A memory that talks about their
// goals is either a copy of the goal list — which is in every context already — or a note that will
// disagree with it the moment a goal is reworded, achieved or removed. Hers said "Current active goals
// are: …" and she read it back over the real list for weeks. Word-boundary matched so "målet" counts
// and "måltid" does not.
const GOAL_WORD_PATTERN = /\b(goal|goals|mål|målet|målen)\b/iu

/** Whether a remembered fact talks about the person's goals, and so must not outrank the stored list. */
export const mentionsGoals = (text: string): boolean => GOAL_WORD_PATTERN.test(text)

// Deterministic failures where a retry cannot change the outcome (the transcript / game session
// will not appear between immediate re-queues) — fail fast instead of burning all attempts.
export const AI_COACH_PREGEN_NON_RETRYABLE_ERRORS = ['no-transcript-found', 'no-game-session-found'] as const

export const isNonRetryableAICoachPregenError = (message?: string | null): boolean =>
  !!message && AI_COACH_PREGEN_NON_RETRYABLE_ERRORS.some((error) => message.includes(error))
